import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  ChevronRightIcon,
  EnvelopeIcon,
  PhoneIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { Card, PolicyNumber } from '@/components/common';
import api from '@/services/api';
import { ContactRow } from './portalProfile';

/**
 * C2 / B-05 — "Your advisors", every broker and agent across ALL the
 * customer's policies.
 *
 * The old card pair showed exactly one broker and one agent, read off the
 * single `Customer` row `/customers/me/` happens to pick. `Customer.user` is a
 * FK on the server, so one login holds rows — and policies — at several
 * brokers, and two policies at one broker can be sold by two different agents;
 * the customer was being shown one of them and given no way to reach the rest.
 *
 * `GET /customers/me/advisors/` answers the whole book as a tree: a broker per
 * node with its agents beneath it, and, per advisor, the policies they cover.
 * Its own query rather than a key inside `/customers/me/` — so it gets its own
 * loading, error and empty states, and so the screens that fetch `me` for the
 * profile or the avatar do not pay for the policy walk.
 *
 * Policy numbers go through the shared `PolicyNumber` helper, which leads with
 * the insurer's number and labels ours "internal ref". The server serves both
 * numbers for exactly that reason.
 */

export interface AdvisorPolicy {
  id: string;
  policy_number: string;
  insurer_policy_number: string;
  insurer_name: string | null;
}

export interface AdvisorAgent {
  id: string;
  name: string;
  phone: string;
  email: string;
  /** True when a Broker Admin assigned them to this customer, policy or not. */
  assigned: boolean;
  policies: AdvisorPolicy[];
}

export interface AdvisorBroker {
  id: string;
  name: string;
  phone: string;
  email: string;
  policies: AdvisorPolicy[];
  agents: AdvisorAgent[];
}

export function useMyAdvisors() {
  return useQuery<AdvisorBroker[]>({
    queryKey: ['customer-me-advisors'],
    queryFn: async () => {
      const res = await api.get('/customers/me/advisors/');
      return res.data?.advisors ?? [];
    },
  });
}

function PoliciesCovered({ policies, none }: { policies: AdvisorPolicy[]; none: string }) {
  if (policies.length === 0) {
    return <p className="mt-3 text-sm italic text-gray-400 dark:text-gray-500">{none}</p>;
  }
  return (
    <div className="mt-3">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Policies covered
      </p>
      <ul className="mt-1 space-y-1">
        {policies.map((policy) => (
          <li key={policy.id} className="text-sm text-gray-900 dark:text-white">
            <PolicyNumber
              source={policy}
              render={(text) => (
                <Link
                  to={`/policies/${policy.id}`}
                  className="font-medium text-primary-600 hover:underline dark:text-primary-400"
                >
                  {text}
                </Link>
              )}
            />
            {policy.insurer_name && (
              <span className="ml-2 text-gray-500 dark:text-gray-400">{policy.insurer_name}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * One advisor. The name and role are always visible so the customer can see the
 * shape of the tree at a glance; the contact details and the policies are
 * behind the disclosure, because a customer with policies at three brokers
 * would otherwise scroll past a wall of phone numbers to find the one they want.
 */
function AdvisorNode({
  testId,
  panelId,
  icon,
  name,
  role,
  note,
  phone,
  email,
  policies,
  noPolicies,
  children,
}: {
  testId: string;
  panelId: string;
  icon: React.ReactNode;
  name: string;
  role: string;
  note?: string;
  phone?: string;
  email?: string;
  policies: AdvisorPolicy[];
  noPolicies: string;
  children?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const hasContact = !!(phone || email);

  return (
    <li data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex w-full items-center gap-3 rounded-lg px-2 py-3 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:bg-gray-700/50"
      >
        <ChevronRightIcon
          className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`}
          aria-hidden="true"
        />
        <span className="flex-shrink-0 text-gray-400 dark:text-gray-500">{icon}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-gray-900 dark:text-white">{name}</span>
          <span className="block text-xs text-gray-500 dark:text-gray-400">
            {role}
            {note ? ` · ${note}` : ''}
          </span>
        </span>
        <span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">
          {policies.length === 1 ? '1 policy' : `${policies.length} policies`}
        </span>
      </button>

      {open && (
        <div id={panelId} className="border-l-2 border-gray-100 pb-2 pl-6 ml-4 dark:border-gray-700">
          {hasContact ? (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              <ContactRow icon={<UserIcon className="h-4 w-4" />} label="Name" value={name} />
              <ContactRow icon={<PhoneIcon className="h-4 w-4" />} label="Phone" value={phone || undefined} />
              <ContactRow icon={<EnvelopeIcon className="h-4 w-4" />} label="Email" value={email || undefined} />
            </div>
          ) : (
            <p className="py-2 text-sm italic text-gray-400 dark:text-gray-500">
              No contact details recorded — ask your broker.
            </p>
          )}
          <PoliciesCovered policies={policies} none={noPolicies} />
        </div>
      )}

      {children}
    </li>
  );
}

export function CustomerAdvisors() {
  const { data, isLoading, isError, refetch } = useMyAdvisors();
  const brokers = data ?? [];

  return (
    <Card>
      <div className="p-6">
        {isLoading ? (
          <div className="animate-pulse space-y-3" aria-label="Loading your advisors">
            {[0, 1].map((i) => (
              <div key={i} className="h-12 rounded-lg bg-gray-100 dark:bg-gray-700" />
            ))}
          </div>
        ) : isError ? (
          <div className="py-2 text-sm">
            <p className="text-gray-500 dark:text-gray-400">Failed to load your advisors.</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-2 font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              Try again
            </button>
          </div>
        ) : brokers.length === 0 ? (
          <p className="py-2 text-sm text-gray-500 dark:text-gray-400">
            No advisors yet. They appear here once you hold a policy with a broker, or one of their agents is
            assigned to you.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {brokers.map((broker) => (
              <AdvisorNode
                key={broker.id}
                testId="advisor-broker"
                panelId={`advisor-broker-${broker.id}`}
                icon={<BuildingOfficeIcon className="h-5 w-5" />}
                name={broker.name}
                role="Broker"
                phone={broker.phone}
                email={broker.email}
                policies={broker.policies}
                noPolicies="No policies with this broker yet."
              >
                {broker.agents.length > 0 && (
                  <ul className="ml-6 border-l border-gray-100 pl-2 dark:border-gray-700">
                    {broker.agents.map((agent) => (
                      <AdvisorNode
                        key={agent.id}
                        testId="advisor-agent"
                        panelId={`advisor-agent-${agent.id}`}
                        icon={<UserIcon className="h-5 w-5" />}
                        name={agent.name}
                        role="Agent"
                        // The one thing the tree cannot show by shape: an agent
                        // the broker assigned to this customer, who has not
                        // sold them anything, is still their agent.
                        note={agent.assigned ? 'assigned to you' : undefined}
                        phone={agent.phone}
                        email={agent.email}
                        policies={agent.policies}
                        noPolicies="No policies through this agent yet."
                      />
                    ))}
                  </ul>
                )}
              </AdvisorNode>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
